import { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText,
    ListSubheader,
    TextField,
    InputAdornment,
    Typography
} from '@mui/material';
import { SearchOutlined } from '@mui/icons-material';

const SearchableSelect = ({ label, value, onChange, onBlur, options, disabled, error, helperText, required, icon: Icon }) => {
    const [search, setSearch] = useState('');

    const filtered = useMemo(() => options?.filter((o) => o.name?.toLowerCase().includes(search.toLowerCase())), [options, search]);

    return (
        <FormControl fullWidth error={error} disabled={disabled}>
            <InputLabel required={required}>{label}</InputLabel>
            <Select
                value={value}
                label={label}
                onChange={onChange}
                onBlur={onBlur}
                onClose={() => setSearch('')}
                startAdornment={
                    Icon ? (
                        <InputAdornment position="start">
                            <Icon fontSize="small" color={error ? 'error' : 'action'} />
                        </InputAdornment>
                    ) : undefined
                }
                MenuProps={{ autoFocus: false, PaperProps: { style: { maxHeight: 300 } } }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 }, borderRadius: 2 }}
            >
                {/* Inline search box */}
                <ListSubheader sx={{ pt: 1, pb: 0.5, bgcolor: 'background.paper' }}>
                    <TextField
                        size="small"
                        placeholder={`Search ${label}...`}
                        fullWidth
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchOutlined fontSize="small" />
                                </InputAdornment>
                            )
                        }}
                    />
                </ListSubheader>

                {filtered.length === 0 ? (
                    <MenuItem disabled>
                        <Typography variant="body2" color="text.secondary">
                            No results found
                        </Typography>
                    </MenuItem>
                ) : (
                    filtered.map((opt) => (
                        <MenuItem key={opt.id} value={opt.id}>
                            {opt.name}
                        </MenuItem>
                    ))
                )}
            </Select>
            {helperText && <FormHelperText>{helperText}</FormHelperText>}
        </FormControl>
    );
};

SearchableSelect.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    onBlur: PropTypes.func,
    options: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, name: PropTypes.string })).isRequired,
    disabled: PropTypes.bool,
    error: PropTypes.bool,
    helperText: PropTypes.string,
    required: PropTypes.bool,
    icon: PropTypes.elementType
};

SearchableSelect.defaultProps = {
    onBlur: undefined,
    disabled: false,
    error: false,
    helperText: '',
    required: false,
    icon: undefined
};

export default SearchableSelect;
